import { useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import {Instagram, Menu, Send, X} from 'lucide-react'
import {
  ADDRESS_LINE,
  INSTAGRAM_URL,
  SITE_CNPJ,
  SITE_LEGAL_NAME,
  TELEGRAM_URL,
  WA_DEFAULT,
  WHATSAPP_DISPLAY,
} from '@/lib/site'

const nav = [
  { to: '/', label: 'Início' },
  { to: '/perfis-disponiveis', label: 'Perfis disponíveis' },
  { to: '/como-funciona', label: 'Como funciona' },
  { to: '/sobre', label: 'Sobre nós' },
  { to: '/blog', label: 'Blog' },
  { to: '/contato', label: 'Contato' },
]

function LogoMark() {
  return (
    <Link to="/" className="flex items-center gap-3 group">
      <span className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-[#0c1220] ring-1 ring-white/10">
        <span className="ig-gradient absolute inset-[1px] rounded-[7px] opacity-90" />
        <span className="relative font-display text-lg font-medium leading-none text-white">M</span>
      </span>
      <span className="leading-tight">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-white/45">Desde 2019</span>
        <span className="block text-[15px] font-semibold tracking-tight text-white">Muita Mídia</span>
      </span>
    </Link>
  )
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7zM12.05 20.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.14.82.84-3.06-.2-.31a8.18 8.18 0 0 1-1.26-4.36c0-4.52 3.68-8.2 8.21-8.2 2.19 0 4.25.85 5.8 2.4a8.16 8.16 0 0 1 2.4 5.8c0 4.53-3.68 8.24-8.16 8.24zm4.5-6.15c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.12-1.06-.39-2.02-1.25-.75-.66-1.25-1.48-1.4-1.73-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.42h-.48c-.16 0-.43.06-.65.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.17.2-.58.2-1.07.14-1.17-.06-.11-.23-.17-.48-.29z" />
    </svg>
  )
}

export default function Layout() {
  const [open, setOpen] = useState(false)

  return (
    <div className="min-h-screen bg-[#070b14] text-[#e8edf7]">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[80] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:text-black"
      >
        Pular para o conteúdo
      </a>

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#070b14]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-4 sm:px-6">
          <LogoMark />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Principal">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition ${
                    isActive ? 'text-white' : 'text-white/55 hover:text-white'
                  }`
                }
                end={item.to === '/'}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-[#070b14] transition hover:bg-orange-100 sm:inline-flex"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp
            </a>
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white lg:hidden"
              aria-label={open ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open ? (
          <div className="border-t border-white/10 bg-[#070b14] px-4 py-4 lg:hidden">
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-sm ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`
                  }
                  end={item.to === '/'}
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={WA_DEFAULT}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-semibold text-[#070b14]"
              >
                Falar no WhatsApp
              </a>
            </nav>
          </div>
        ) : null}
      </header>

      <main id="conteudo">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-[#05080f]">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4">
          <div className="md:col-span-2">
            <LogoMark />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/55">
              Especialista em venda de perfis nichados do Instagram desde 2019. Seguidores reais e orgânicos, de 1k a 900k, com transferência assistida e empresa registrada em Belo Horizonte.
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Navegação</p>
            <ul className="mt-4 space-y-2 text-sm text-white/65">
              {nav.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-white">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/privacidade" className="hover:text-white">
                  Política de privacidade
                </Link>
              </li>
              <li>
                <Link to="/termos" className="hover:text-white">
                  Termos de uso
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Empresa</p>
            <p className="mt-4 text-sm leading-relaxed text-white/65">
              {SITE_LEGAL_NAME}
              <br />
              CNPJ {SITE_CNPJ}
              <br />
              {ADDRESS_LINE}
              <br />
              WhatsApp {WHATSAPP_DISPLAY}
            </p>
            <div className="mt-4 flex gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram da Muita Mídia"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 hover:text-white"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram da Muita Mídia"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 hover:text-white"
              >
                <Send className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <p className="mx-auto max-w-6xl px-4 py-5 text-xs text-white/40 sm:px-6">
            {SITE_LEGAL_NAME} · CNPJ {SITE_CNPJ} · {ADDRESS_LINE}
          </p>
        </div>
      </footer>

      <a
        href={WA_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar com especialista no WhatsApp"
        className="fixed bottom-5 right-5 z-50 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-[#073b1d] shadow-[0_12px_40px_rgba(37,211,102,0.35)] transition hover:scale-105"
      >
        <WhatsAppIcon className="h-7 w-7" />
      </a>
    </div>
  )
}

export { WhatsAppIcon }
